import React from "react";
import type {
  FieldError as HookFieldError,
  FieldValues,
  Path,
  UseFormSetError,
} from "react-hook-form";
import { getApiFieldErrors, getErrorMessage } from "@/services/api";

/**
 * Shows server errors for `fields` under their inputs and returns any other error
 * as one message for the page banner (null when every error found its field).
 */
export function showServerErrors<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
  fields: Path<T>[]
): string | null {
  const fieldErrors = getApiFieldErrors(error);
  const shown = fields.filter((field) => fieldErrors[field]);
  shown.forEach((field) => setError(field, { type: "server", message: fieldErrors[field] }));
  return shown.length ? null : getErrorMessage(error);
}

/** Props that tie an input to its error text for screen readers. */
export const describedBy = (id: string, error: HookFieldError | undefined) => ({
  "aria-invalid": !!error,
  "aria-describedby": error ? `${id}-error` : undefined,
});

export const FieldError: React.FC<{ id: string; error: HookFieldError | undefined }> = ({
  id,
  error,
}) =>
  error ? (
    <p id={`${id}-error`} className="text-sm text-destructive">
      {error.message}
    </p>
  ) : null;
